import * as React from "react";

import type { SpecType } from "@/api/contracts/specifications";
import type { ItemDialogSeed } from "@/components/catalog/item-detail-dialog";
import { BaseItemPicture } from "@/components/ui/base-item-picture";
import { StaticChip } from "@/components/ui/chip";
import { ChangedFromBase } from "@/components/ui/changed-from-base";
import { HeroImageBox } from "@/components/ui/hero-image-box";
import { MuiBasicTableCell, MuiBasicTableRow } from "@/components/ui/mui-basic-table";
import { MuiLink } from "@/components/ui/mui-link";
import { OldBesideNew, OldValueAlone } from "@/components/ui/old-beside-new";
import { PushBadge } from "@/components/ui/push-badge";
import { RemovedRoomNotes, RoomChangeList } from "@/components/ui/room-change-list";
import { TruncatedNotes } from "@/components/ui/truncated-notes";
import type { SpecLinesLine, SpecLinesSelection } from "@/hooks/specifications";
import {
  NOT_PER_PLAN_TEXT,
  OLD_NOTES_MAX,
  PER_PLAN_TEXT,
  roomNotesPair,
  rowBasePairs,
  rowChangeSentence,
  type OldValue,
  type PairRowKind,
  type RowBasePairs,
} from "@/lib/spec-base-changes";
import {
  pushRowBadge,
  pushRowSentence,
  wholeRowKind,
  type SpecLinePush,
} from "@/lib/spec-push-preview";
import {
  ITEM_NAME_MAX,
  heroBadges,
  isRowChanged,
  lineDisplayName,
  locationsText,
  rowBackground,
  shownSelections,
} from "@/lib/spec-lines-display";
import { MuiTypography } from "@/components/ui/mui-typography";
import { cn } from "@/lib/utils";

// One line of a spec version as the details page draws it — the donor's
// `LineDetails`, `LineDetailsParentRow`, `LineDetailsRow`, `PerPlanDetails` and
// `MobileViewLineDetails` (`SpecDetailsIsland/components/MainContent/Lines/`),
// with the spec sheets' `ReadOnlyLocationNotes` (#695).
//
// A line draws one row when it shows one selection, and a parent row with one
// indented child row per selection when it shows more. Which selections it
// shows is the changes switch's call: all of them with it on, the alive ones
// otherwise — so a line whose other selections are deleted flips to the tree
// when the switch turns on. A deleted line draws nothing at all, whatever the
// switch says (only the Editor strikes deleted lines through).
//
// Below `md` (900px) each selection is the donor's four stacked rows instead,
// and what the donor drops there is dropped here: JDE, model, the chip, every
// tint and the strikethrough, the changes cell, Missing Image, the deleted and
// Retired marks, the deep link's scroll.
//
// **The old beside the new** (#1083; ledger §7, "Changed from base widens"):
// with "Show changes from Base" on, a desktop row draws each changed value as
// its base value struck, an arrow, then the value as drawn without the switch
// (`OldBesideNew`), per the #1043 brief's per-field table — the names, the
// locations line and its rooms, the item swap's ghost picture and its maker,
// name, JDE and model, per-plan both ways, the paid-upgrade mark, the per-plan
// manufacturer, the row's notes and its rooms' notes, a one-selection row's
// changed selection notes and the notes of rooms its line lost (#1106 D7,
// D8). Which values pair is
// `rowBasePairs`'s rule, and the icon's sentence `rowChangeSentence`'s; the
// base values are the server's. Below `md` none of it draws, as the tint and
// the changes cell don't.
//
// **The push preview's rows** (#1090; #1049 R2, R4–R8; ledger §7, "Named
// scope additions (Q13–Q15)"): with `push` set, the rows are the community's
// as they will read after a push, and the push's facts decide the marks in
// place of the page's own flags — only the selections the push touches draw
// under a line's own row (R4); a row is tinted, and its info icon names the
// push's change ("Changed by Coastal V8: Line notes, Item."), exactly when the
// push touches it (R2, R8); a row the push adds or removes says so in a badge
// under its name (R5, R6); a moved line names the group it leaves above its
// name (R7); and a line the push removes is drawn — the page draws a deleted
// line as nothing — struck, with any struck row, in the deleted-row red.
// Unset, none of it draws: the details page and the Editor are unchanged.
//
// The parity enumeration on #695 names the source line of every value below.

const CHILD_OF_ICON = "/child-of.svg";

const BACKGROUND_CLASS = {
  highlight: "bg-spec-highlight-blue",
  "paid-upgrade": "bg-spec-paid-upgrade",
  changed: "bg-spec-changed-from-base",
  none: "bg-transparent",
} as const;

/** The deep link's scroll waits for the group to expand (`LineDetailsRow.tsx:58-68`). */
const HIGHLIGHT_SCROLL_DELAY_MS = 500;

export interface SpecLineRowsProps {
  line: SpecLinesLine;
  /** The spec's type: the community pointer clause and the info sentence's word. */
  specType: SpecType;
  /** "Show changes from Base". */
  showChanges: boolean;
  /**
   * The deep link's selection, when `?lineId=` is set too — the donor tests
   * both params and then the selection alone, never the line.
   */
  highlightSelectionId: number | null;
  /** At `md` and up: the table rows. Below it: the stacked mobile rows. */
  isDesktop: boolean;
  /** Opens the read-only item dialog. */
  onOpenItem: (item: ItemDialogSeed) => void;
  /** The push preview's facts for this line (#1090); unset, the page's rows. */
  push?: SpecLinePush;
}

export function SpecLineRows(props: SpecLineRowsProps) {
  const { line, showChanges, push } = props;
  // The preview draws a line its push removes; the page never draws one.
  if (line.deleted && !push) return null;

  const selections = shownSelections(line.selections, showChanges);
  if (selections.length > 1) {
    // R4: a line the push changes in part keeps its own row over only the
    // selections it touches; one it adds or removes brings them all.
    const children =
      push && wholeRowKind(push.facts.kinds) === null
        ? selections.filter((selection) => push.selections.has(selection.id))
        : selections;
    return (
      <>
        <ParentRow {...props} />
        {children.map((selection) => (
          <SelectionRow key={selection.id} {...props} selection={selection} isSelectionRow />
        ))}
      </>
    );
  }
  // One selection — or none, when the switch is off and every selection is
  // deleted: the donor still draws the row, over no selection.
  return <SelectionRow {...props} selection={selections[0]} isSelectionRow={false} />;
}

function ParentRow({ line, specType, showChanges, isDesktop, push }: SpecLineRowsProps) {
  const locations = locationsText(line.locationType, line.locations);
  const hasLocationNotes = line.locations.some((location) => location.notes);

  if (!isDesktop) {
    return (
      <MuiBasicTableRow data-testid="row-detail-line-mobile">
        <MuiBasicTableCell colSpan={7} className="px-4">
          <LineHeading name={line.name} locations={locations} />
        </MuiBasicTableCell>
        <MuiBasicTableCell className="px-4 [overflow-wrap:break-word]">
          <TruncatedNotes notes={line.notes} />
        </MuiBasicTableCell>
      </MuiBasicTableRow>
    );
  }

  const pairs = basePairs(showChanges, line, undefined, specType, "parent");
  // R8: the line's own row takes the tint and the icon when the push changes
  // the line itself; one whose only change is under it stays plain.
  const sentence = push
    ? pushRowSentence(push.by, push.facts, line, undefined, undefined, "parent")
    : null;
  return (
    <MuiBasicTableRow
      data-testid="row-detail-line"
      className={cn(
        hasLocationNotes && "[&>td]:border-b-0",
        sentence && BACKGROUND_CLASS.changed,
        push && line.deleted && REMOVED_ROW_CLASS,
      )}
    >
      <MuiBasicTableCell colSpan={7} className="pl-6">
        <LineHeading
          name={<Paired old={pairs.name}>{line.name}</Paired>}
          locations={<LocationsLine text={locations} pairs={pairs} />}
          move={push?.facts.move}
          badge={push ? wholeRowKind(push.facts.kinds) : null}
        />
      </MuiBasicTableCell>
      <MuiBasicTableCell className="text-left [overflow-wrap:break-word]">
        <NotesPair old={pairs.notes} notes={line.notes}>
          <TruncatedNotes notes={line.notes} />
        </NotesPair>
      </MuiBasicTableCell>
      {showChanges ? (
        <ChangesCell>
          {sentence ? <ChangedFromBase specType={specType} sentence={sentence} /> : null}
        </ChangesCell>
      ) : null}
    </MuiBasicTableRow>
  );
}

/**
 * The line's name in `body1`/500 over its locations in `body2`, 2.4px apart —
 * in the push preview, under a move's groups and over an added or removed
 * badge.
 */
function LineHeading({
  name,
  locations,
  move = null,
  badge = null,
}: {
  name: React.ReactNode;
  locations: React.ReactNode;
  move?: { from: string; to: string } | null;
  badge?: "added" | "removed" | null;
}) {
  return (
    <div className="flex flex-col gap-[2.4px]">
      {move ? <GroupMove move={move} /> : null}
      <MuiTypography className="font-medium">{name}</MuiTypography>
      <MuiTypography variant="body2">{locations}</MuiTypography>
      {badge ? <PushBadge kind={badge} /> : null}
    </div>
  );
}

/** The push preview's struck row: the Editor's deleted-row red (Assumption 8). */
const REMOVED_ROW_CLASS = "text-spec-row-deleted decoration-spec-row-deleted line-through";

/**
 * R7: a line the push moves names the group it leaves, above its name, in the
 * pair's own look — "Group: ~~Hardware Misc~~ → Plumbing".
 */
function GroupMove({ move }: { move: { from: string; to: string } }) {
  return (
    <span
      data-testid="push-move"
      className="text-lennar-ink-secondary block pb-0.5 text-[0.875rem] leading-snug"
    >
      Group: <OldBesideNew old={move.from}>{move.to}</OldBesideNew>
    </span>
  );
}

/**
 * The donor's `SvgImage` — react-ui's `Box component='img'` sized 17×17 in CSS,
 * so the 20×18 SVG is stretched square and the image stays inline on the text
 * baseline (`LineDetailsRow.tsx:243`, `MobileViewLineDetails.tsx:38`). Preflight's
 * `display: block; height: auto` would keep its aspect and stack it, 6.7px
 * shorter per selection on a phone (#1064 F1). Exported for "Where to save"'s
 * child rows (#1217), which draw the same glyph the same way.
 */
export function ChildOfIcon({ className }: { className?: string }) {
  return (
    <img
      src={CHILD_OF_ICON}
      alt=""
      width={17}
      height={17}
      className={cn("inline size-[17px] align-baseline", className)}
    />
  );
}

interface SelectionRowProps extends SpecLineRowsProps {
  selection: SpecLinesSelection | undefined;
  isSelectionRow: boolean;
}

function SelectionRow({
  line,
  selection,
  isSelectionRow,
  specType,
  showChanges,
  highlightSelectionId,
  isDesktop,
  onOpenItem,
  push,
}: SelectionRowProps) {
  const rowRef = React.useRef<HTMLTableRowElement>(null);
  const highlighted = highlightSelectionId !== null && selection?.id === highlightSelectionId;

  React.useEffect(() => {
    if (!highlighted || !rowRef.current) return;
    const timer = window.setTimeout(() => {
      rowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, HIGHLIGHT_SCROLL_DELAY_MS);
    // A row gone before the delay — a search replacing it — has nothing to
    // scroll to.
    return () => window.clearTimeout(timer);
  }, [highlighted]);

  // A deleted selection shows only with "Show changes from Base" on, and then
  // as deleted: struck through, and the thumbnail's deleted glyph — as is
  // every row of a line the push removes.
  const shownDeleted =
    (Boolean(selection?.deleted) && showChanges) || (push !== undefined && line.deleted);
  if (selection?.deleted && !showChanges) return null;

  const item = selection?.item ?? null;
  const notes = isSelectionRow ? selection?.notes : line.notes;
  const lineName = isSelectionRow ? line.name : lineDisplayName(line.name, selection?.name);
  const locations = locationsText(line.locationType, line.locations);
  const openItem = () => {
    // The row's own item, not just its id: the dialog draws the name and hero
    // from it while the full read is in flight, as the donor's does (#718 B1).
    if (item) onOpenItem({ id: item.id, name: item.name, heroImage: item.heroImage });
  };

  if (!isDesktop) {
    return (
      <MobileSelectionRows
        rowRef={rowRef}
        line={line}
        selection={selection}
        isSelectionRow={isSelectionRow}
        lineName={lineName}
        locations={locations}
        notes={notes}
        highlighted={highlighted}
        struck={shownDeleted}
        onOpen={openItem}
      />
    );
  }

  const row = isSelectionRow ? "child" : "single";
  const selectionKind = push && selection ? push.selections.get(selection.id) : undefined;
  // In the preview the push's change marks the row, not the page's flags.
  const pushSentence = push
    ? pushRowSentence(push.by, push.facts, line, selection, selectionKind, row)
    : null;
  const changed = push ? pushSentence !== null : isRowChanged(line, selection, specType);
  const showRowChange = changed && showChanges;
  const badge = push ? pushRowBadge(push.facts, selectionKind, row) : null;
  const isPendingApproval = item?.isApproved === false;
  const pairs = basePairs(showChanges, line, selection, specType, row);
  const hero = (
    <HeroImageBox
      image={item?.heroImage}
      perPlan={line.perPlan}
      alt={item?.name}
      onClick={openItem}
      isPendingApproval={isPendingApproval}
      isDeleted={shownDeleted}
      isRetired={Boolean(item?.retired)}
      {...heroBadges(line, item)}
    />
  );

  return (
    <MuiBasicTableRow
      ref={rowRef}
      data-testid={`row-detail-selection-${selection?.id}`}
      className={cn(
        shownDeleted && (push ? REMOVED_ROW_CLASS : "line-through"),
        isSelectionRow && "[&>td]:border-t-0",
        BACKGROUND_CLASS[
          rowBackground({
            highlighted,
            paidUpgrade: Boolean(selection?.isPaidUpgrade),
            changed: showRowChange,
          })
        ],
      )}
    >
      <MuiBasicTableCell className="w-[10%] min-w-[215px] pl-6">
        <div className="flex flex-row items-center">
          {isSelectionRow ? <ChildOfIcon className="mr-4" /> : null}
          <div className="flex flex-col gap-[2.4px]">
            {isSelectionRow ? (
              <>
                <MuiTypography variant="body2">
                  <Paired old={pairs.name}>{selection?.name ?? ""}</Paired>
                </MuiTypography>
                {badge ? <PushBadge kind={badge} /> : null}
              </>
            ) : (
              <LineHeading
                name={<Paired old={pairs.name}>{lineName}</Paired>}
                locations={<LocationsLine text={locations} pairs={pairs} />}
                move={push?.facts.move}
                badge={badge}
              />
            )}
          </div>
        </div>
      </MuiBasicTableCell>

      <MuiBasicTableCell>
        <PaidUpgradeMark
          change={pairs.paidUpgrade}
          chip={
            selection?.isPaidUpgrade ? (
              <StaticChip label="Paid upgrade" className="bg-mui-success" />
            ) : null
          }
        />
      </MuiBasicTableCell>

      <MuiBasicTableCell size="small" align="center" className="w-[10%]">
        <div
          data-testid={`selection-${item?.id}-image`}
          className={cn("pt-1", item ? "cursor-pointer" : "cursor-default")}
        >
          <GhostedPicture ghost={pairs.ghost} selection={selection}>
            {hero}
          </GhostedPicture>
        </div>
      </MuiBasicTableCell>

      {line.perPlan ? (
        <>
          <MuiBasicTableCell colSpan={4}>
            <PerPlanDetails selection={selection} pairs={pairs} />
          </MuiBasicTableCell>
          <MuiBasicTableCell colSpan={3}>
            <NotesAndLocationNotes
              notes={notes}
              selectionNotes={selection?.notes}
              line={line}
              pairs={pairs}
            />
          </MuiBasicTableCell>
        </>
      ) : (
        <>
          <MuiBasicTableCell className="w-[15%] max-w-[100px] min-w-[40px] [overflow-wrap:break-word]">
            <Paired old={pairs.manufacturer} layout="block">
              {item?.manufacturer.name ?? ""}
            </Paired>
          </MuiBasicTableCell>
          <MuiBasicTableCell className="w-[15%] max-w-[140px] min-w-[100px]">
            <Paired old={pairs.itemName} layout="block" cutAt={ITEM_NAME_MAX}>
              {item ? (
                <ItemNameLink onClick={openItem} struck={shownDeleted}>
                  {item.name || "Item Name"}
                </ItemNameLink>
              ) : (
                <MuiTypography variant="body2" color="secondary">
                  No Item Selected
                </MuiTypography>
              )}
            </Paired>
          </MuiBasicTableCell>
          <MuiBasicTableCell className="w-[12%] max-w-[80px] min-w-[40px] [overflow-wrap:break-word]">
            <Paired old={pairs.jde}>{item?.jdeItemNumber ?? ""}</Paired>
          </MuiBasicTableCell>
          <MuiBasicTableCell className="w-[11%] max-w-[80px] min-w-[35px] [overflow-wrap:break-word]">
            <Paired old={pairs.model}>{item?.modelNumber ?? ""}</Paired>
          </MuiBasicTableCell>
          <MuiBasicTableCell
            align="center"
            className="relative w-[38%] max-w-[100px] min-w-[70px] [overflow-wrap:break-word]"
          >
            <NotesAndLocationNotes
              notes={notes}
              selectionNotes={selection?.notes}
              line={line}
              pairs={pairs}
            />
          </MuiBasicTableCell>
        </>
      )}

      {showChanges ? (
        <ChangesCell>
          {showRowChange ? (
            <ChangedFromBase
              specType={specType}
              sentence={
                (push ? pushSentence : rowChangeSentence(line, selection, specType)) ?? undefined
              }
              className={line.status === "finalized" ? "mr-2" : undefined}
            />
          ) : null}
        </ChangesCell>
      ) : null}
    </MuiBasicTableRow>
  );
}

/** The trailing cell the changes switch adds: no left padding, a top rule. */
function ChangesCell({ children }: { children?: React.ReactNode }) {
  return (
    <MuiBasicTableCell className="border-lennar-table-rule border-t border-solid pl-0">
      {children}
    </MuiBasicTableCell>
  );
}

/** A row's pairs: none with the switch off (D1). */
function basePairs(
  showChanges: boolean,
  line: SpecLinesLine,
  selection: SpecLinesSelection | undefined,
  specType: SpecType,
  kind: PairRowKind,
): RowBasePairs {
  return showChanges ? rowBasePairs(line, selection, specType, kind) : {};
}

/**
 * A value drawn as without the switch, or — where its row pairs it — its old
 * value struck beside it. `cutAt` cuts a long old value for the eye only. The
 * Editor's rows draw it too (#1104).
 */
export function Paired({
  old,
  layout,
  cutAt,
  children,
}: {
  old: OldValue | undefined;
  layout?: "measured" | "block";
  cutAt?: number;
  /** The current value; none reads *None*. */
  children?: React.ReactNode;
}) {
  if (old === undefined) return <>{children}</>;
  return (
    <OldBesideNew old={old} cutAt={cutAt} layout={layout}>
      {children}
    </OldBesideNew>
  );
}

/**
 * The locations line under a line's name: the plain text, or a specific
 * line's changed rooms in its place, under the old locations text on a type
 * change.
 */
function LocationsLine({ text, pairs }: { text: string; pairs: RowBasePairs }) {
  const now = pairs.rooms ? (
    <RoomChangeList rooms={pairs.rooms.current} removed={pairs.rooms.removed} />
  ) : (
    text
  );
  return (
    <Paired old={pairs.locations} layout="block">
      {now}
    </Paired>
  );
}

/** The paid-upgrade cell: the chip, its newly set mark, or a cleared one's struck text. */
function PaidUpgradeMark({
  change,
  chip,
}: {
  change: RowBasePairs["paidUpgrade"];
  chip: React.ReactNode;
}) {
  if (change === "set") return <OldBesideNew old="Not a paid upgrade">{chip}</OldBesideNew>;
  if (change === "cleared") return <OldValueAlone>Paid upgrade</OldValueAlone>;
  return <>{chip}</>;
}

/** The ghost's picture: the base item's, the selection's own item's, or the per-plan illustration. */
function ghostPicture(
  ghost: NonNullable<RowBasePairs["ghost"]>,
  selection: SpecLinesSelection | undefined,
) {
  if (ghost === "per-plan")
    return { image: null, perPlan: true, name: "the per-plan illustration" };
  const item = ghost === "base-item" ? selection?.base?.item : selection?.item;
  return { image: item?.heroImage, name: item?.name ?? "" };
}

/**
 * A row's picture, or — where its row pairs it — the ghost of the base side's
 * picture struck and small above it. The Editor's rows draw it too (#1104).
 */
export function GhostedPicture({
  ghost,
  selection,
  children,
}: {
  ghost: RowBasePairs["ghost"];
  selection: SpecLinesSelection | undefined;
  /** The current picture, as drawn without the switch. */
  children: React.ReactNode;
}) {
  if (!ghost) return <>{children}</>;
  return <BaseItemPicture {...ghostPicture(ghost, selection)}>{children}</BaseItemPicture>;
}

/**
 * Notes, or — where its row pairs them — the old notes' text struck above
 * them with the elbow, cut at 140, *None* on an empty side. The Editor's notes
 * rows draw it too (#1104).
 */
export function NotesPair({
  old,
  notes,
  className,
  children,
}: {
  old: OldValue | undefined;
  notes: string | null | undefined;
  className?: string;
  children: React.ReactNode;
}) {
  if (old === undefined) return <>{children}</>;
  return (
    <div className={cn("text-left", className)}>
      <Paired old={old} layout="block" cutAt={OLD_NOTES_MAX}>
        {notes ? children : null}
      </Paired>
    </div>
  );
}

/**
 * The item's name as the donor draws it: MUI `Link` at `color='inherit'`, the
 * cell's `body2`, underlined, a pointer — an anchor with no `href`, so a
 * keyboard never reaches it in modsy. A `<button>` here, dressed identically
 * (#695 D3, an a11y-only break with no pixel moved). The Editor's rows draw
 * it too, bare: the donor's `sx` there is `textDecoration: 'none'` (#809).
 */
export function ItemNameLink({
  onClick,
  struck = false,
  underline = true,
  children,
}: {
  /** None for a selection with no item: the donor's link, drawn the same, that opens nothing. */
  onClick: (() => void) | undefined;
  /**
   * The row's strikethrough. The donor's `<a>` takes it from the row; a button
   * is an atomic inline, which a parent's decoration never reaches, so it draws
   * its own beside the underline.
   */
  struck?: boolean;
  /** MUI's `always` underline — the details rows'; the Editor's has none. */
  underline?: boolean;
  children: React.ReactNode;
}) {
  const decoration = underline ? "always" : "none";
  if (!onClick) {
    return (
      <MuiLink asChild color="inherit" underline={decoration}>
        <span className="cursor-pointer">{children}</span>
      </MuiLink>
    );
  }
  return (
    <MuiLink asChild color="inherit" underline={decoration}>
      <button
        type="button"
        onClick={(event) => {
          // A click leaves focus where the donor's href-less `<a>` leaves it:
          // nowhere, so the item dialog has no opener to hand it back to on
          // closing. The keyboard's Enter (`detail` 0) keeps it — that tab stop
          // is #695 D3's, and the dialog returns there.
          if (event.detail > 0) event.currentTarget.blur();
          onClick();
        }}
        style={
          struck
            ? { textDecorationLine: underline ? "underline line-through" : "line-through" }
            : undefined
        }
        className="cursor-pointer border-0 bg-transparent p-0 text-left [font:inherit]"
      >
        {children}
      </button>
    </MuiLink>
  );
}

/**
 * A per-plan line's item columns — `PerPlanDetails.tsx:12-27`: "Refer to plan
 * for details." and, when the **selection** names one, its manufacturer.
 */
function PerPlanDetails({
  selection,
  pairs = {},
}: {
  selection: SpecLinesSelection | undefined;
  /** The details row's pairs: a line turned per-plan, a changed per-plan manufacturer. */
  pairs?: RowBasePairs;
}) {
  const changedMaker = pairs.perPlanManufacturer !== undefined;
  return (
    <div
      data-testid="per-plan-manufacturer"
      className="flex flex-col items-start justify-center gap-1"
    >
      <MuiTypography variant="body2">
        <Paired old={pairs.perPlan === undefined ? undefined : NOT_PER_PLAN_TEXT}>
          {PER_PLAN_TEXT}
        </Paired>
      </MuiTypography>
      {selection?.manufacturer || changedMaker ? (
        <MuiTypography variant="body2">
          Manufacturer:{" "}
          <Paired old={pairs.perPlanManufacturer}>{selection?.manufacturer?.name ?? ""}</Paired>
        </MuiTypography>
      ) : null}
    </div>
  );
}

/**
 * A notes cell's content, on every row that has one: the row's notes (`pb 8`)
 * when present, then — on a `specific` line — its per-location notes. With its
 * row's pairs: a one-selection row's changed selection notes under the line's
 * (#1106 D7), and the rooms the line lost that had notes last (#1106 D8).
 */
function NotesAndLocationNotes({
  notes,
  selectionNotes,
  line,
  pairs = {},
}: {
  notes: string | null | undefined;
  /** A one-selection row's selection notes, drawn only where they pair. */
  selectionNotes?: string | null;
  line: SpecLinesLine;
  /** The details row's pairs: its notes', and whether its rooms' notes pair. */
  pairs?: RowBasePairs;
}) {
  return (
    <>
      {pairs.notes !== undefined ? (
        <NotesPair old={pairs.notes} notes={notes} className="pb-2">
          <TruncatedNotes notes={notes} />
        </NotesPair>
      ) : notes ? (
        <TruncatedNotes notes={notes} className="pb-2" />
      ) : null}
      {pairs.selectionNotes !== undefined ? (
        <NotesPair old={pairs.selectionNotes} notes={selectionNotes} className="pb-2">
          <TruncatedNotes notes={selectionNotes} />
        </NotesPair>
      ) : null}
      {line.locationType === "specific" ? (
        <ReadOnlyLocationNotes locations={line.locations} pairNotes={pairs.roomNotes === true} />
      ) : null}
      {pairs.removedRoomNotes ? <RemovedRoomNotes rooms={pairs.removedRoomNotes} /> : null}
    </>
  );
}

/**
 * A specific-location line's per-location notes, read-only — the spec sheets'
 * `ReadOnlyLocationNotes`: each location that has notes, its name in `body2`/500
 * over the notes, 8px apart; nothing when none has any.
 */
function ReadOnlyLocationNotes({
  locations,
  pairNotes = false,
}: {
  locations: readonly SpecLinesLine["locations"][number][];
  /**
   * The details row pairs its rooms' notes: each changed one under its base
   * notes, and a room whose note was removed listed too, `~~old~~ ↳ None`
   * where its note would be (#1060 Q9).
   */
  pairNotes?: boolean;
}) {
  const pairOf = (location: (typeof locations)[number]) =>
    pairNotes ? roomNotesPair(location) : undefined;
  const listed = locations.filter((location) => location.notes || pairOf(location) !== undefined);
  if (listed.length === 0) return null;
  return (
    <div className="m-0 bg-transparent">
      {listed.map((location) => (
        <div key={location.id} className="flex flex-col justify-between pb-2 text-center">
          <MuiTypography variant="body2" className="text-left font-medium">
            {location.name}
          </MuiTypography>
          <NotesPair old={pairOf(location)} notes={location.notes}>
            <TruncatedNotes notes={location.notes} className="grow [&_p:first-of-type]:ml-0" />
          </NotesPair>
        </div>
      ))}
    </div>
  );
}

interface MobileSelectionRowsProps {
  /** The deep link's scroll target: the selection's first row. */
  rowRef: React.Ref<HTMLTableRowElement>;
  line: SpecLinesLine;
  selection: SpecLinesSelection | undefined;
  isSelectionRow: boolean;
  lineName: string;
  locations: string;
  notes: string | null | undefined;
  /** The deep link's selection. */
  highlighted: boolean;
  /** A deleted selection with "Show changes from Base" on. */
  struck: boolean;
  onOpen: () => void;
}

/**
 * One selection below `md` — `MobileViewLineDetails.tsx`: four rows, the first
 * three borderless, the last an empty spacer carrying the rule. The thumbnail
 * is not handed the pending flag, so it never shows Missing Image here; the
 * item link reads "Item Name" with no item, and does nothing.
 *
 * Three owner-ruled additions to the donor's mobile rows (#710): the deep
 * link's selection is highlighted and scrolled to (E1), and a deleted
 * selection with the switch on is struck through and wears the deleted glyph
 * (E4), as on desktop. The Retired marker is Atlas's own (#680 Q6).
 */
function MobileSelectionRows({
  rowRef,
  line,
  selection,
  isSelectionRow,
  lineName,
  locations,
  notes,
  highlighted,
  struck,
  onOpen,
}: MobileSelectionRowsProps) {
  const item = selection?.item ?? null;
  // The highlight tints the selection's whole block; the other row tints stay
  // desktop-only, as in the donor.
  const rowClass = cn(highlighted && BACKGROUND_CLASS.highlight, struck && "line-through");
  return (
    <>
      <MuiBasicTableRow
        ref={rowRef}
        data-testid={`row-detail-selection-mobile-${selection?.id}`}
        className={cn("[&>td]:border-0", rowClass)}
      >
        <MuiBasicTableCell colSpan={2}>
          {isSelectionRow ? (
            <>
              <ChildOfIcon className="mr-4" />
              <div className="flex flex-col gap-[2.4px]">
                <MuiTypography variant="body2">{selection?.name}</MuiTypography>
              </div>
            </>
          ) : (
            <LineHeading name={lineName} locations={locations} />
          )}
        </MuiBasicTableCell>
        {/* The row's one cell spans two of the eight columns; the highlight
            paints only behind cells, so it takes the rest of the width. */}
        {highlighted ? <MuiBasicTableCell colSpan={6} /> : null}
      </MuiBasicTableRow>
      <MuiBasicTableRow className={cn("[&>td]:border-0", rowClass)}>
        <MuiBasicTableCell colSpan={2} className="px-4">
          <div className="cursor-pointer pt-1">
            <HeroImageBox
              image={item?.heroImage}
              perPlan={line.perPlan}
              alt={item?.name}
              onClick={onOpen}
              isDeleted={struck}
              isRetired={Boolean(item?.retired)}
              {...heroBadges(line, item)}
            />
          </div>
        </MuiBasicTableCell>
        <MuiBasicTableCell colSpan={6}>
          {line.perPlan ? (
            <PerPlanDetails selection={selection} />
          ) : (
            <div className="flex flex-col gap-[2.4px]">
              <MuiTypography
                variant="body2"
                // After the size, which would otherwise take body2's 1.43 with it.
                className="text-[0.8rem] leading-[1.43] [overflow-wrap:break-word]"
              >
                {item?.manufacturer.name ?? ""}
              </MuiTypography>
              <div>
                <ItemNameLink onClick={item ? onOpen : undefined} struck={struck}>
                  {item?.name || "Item Name"}
                </ItemNameLink>
              </div>
            </div>
          )}
        </MuiBasicTableCell>
      </MuiBasicTableRow>
      <MuiBasicTableRow className={cn("[&>td]:border-0", rowClass)}>
        <MuiBasicTableCell colSpan={8}>
          <div className="flex flex-col gap-[2.4px] px-4">
            <NotesAndLocationNotes notes={notes} line={line} />
          </div>
        </MuiBasicTableCell>
      </MuiBasicTableRow>
      <MuiBasicTableRow className={cn(highlighted && BACKGROUND_CLASS.highlight)}>
        <MuiBasicTableCell colSpan={8} />
      </MuiBasicTableRow>
    </>
  );
}
